import React from 'react';

/**
 * 페이징 관련 유틸리티 함수들
 */

/**
 * 페이징에 필요한 계산을 수행하는 훅
 * @param {Array} data - 전체 데이터 배열
 * @param {number} itemsPerPage - 페이지당 아이템 수 (기본값: 10)
 * @param {number} maxPageButtons - 한 번에 표시할 최대 페이지 버튼 수 (기본값: 3)
 * @returns {Object} 페이징 관련 상태와 함수들
 */
export const usePagination = (data, itemsPerPage = 10, maxPageButtons = 3) => {
    const [currentPage, setCurrentPage] = React.useState(1);
    
    // 전체 페이지 수 계산
    const totalPages = Math.ceil(data.length / itemsPerPage);
    
    // 현재 페이지에 표시할 데이터 계산
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = data.slice(startIndex, endIndex);
    
    // 페이지 버튼 그룹 계산
    const startPage = Math.floor((currentPage - 1) / maxPageButtons) * maxPageButtons + 1;
    const endPage = Math.min(startPage + maxPageButtons - 1, totalPages);
    
    // 페이지 변경 핸들러
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    
    // 다음 그룹으로 이동
    const handleNextGroup = () => {
        if (endPage < totalPages) {
            setCurrentPage(endPage + 1);
        }
    };
    
    // 이전 그룹으로 이동
    const handlePrevGroup = () => {
        if (startPage > 1) {
            setCurrentPage(startPage - 1);
        }
    };
    
    // 페이지 리셋
    const resetPage = () => {
        setCurrentPage(1);
    };
    
    return {
        currentPage,
        totalPages,
        currentItems,
        startIndex,
        endIndex,
        startPage,
        endPage,
        handlePageChange,
        handleNextGroup,
        handlePrevGroup,
        resetPage,
        setCurrentPage
    };
};

/**
 * 필터링과 페이징을 함께 관리하는 훅
 * @param {Array} originalData - 원본 데이터
 * @param {Function} filterFunction - 필터링 함수
 * @param {number} itemsPerPage - 페이지당 아이템 수
 * @param {number} maxPageButtons - 최대 페이지 버튼 수
 * @param {Object} defaultSort - 기본 정렬 설정 { key: 'fieldName', direction: 'asc' | 'desc' }
 * @returns {Object} 필터링과 페이징 관련 상태와 함수들
 */
export const useFilteredPagination = (originalData, filterFunction = null, itemsPerPage = 10, maxPageButtons = 3, defaultSort = { key: null, direction: 'asc' }) => {
    const [filteredData, setFilteredData] = React.useState(originalData);
    const [currentFilter, setCurrentFilter] = React.useState('all');
    const [sortConfig, setSortConfig] = React.useState(defaultSort);
    
    // originalData가 변경될 때 filteredData도 업데이트
    React.useEffect(() => {
        applyFilterAndSort(originalData, currentFilter, sortConfig);
    }, [originalData]); // eslint-disable-line react-hooks/exhaustive-deps
    
    const pagination = usePagination(filteredData, itemsPerPage, maxPageButtons);
    
    // 정렬 함수
    const sortData = (data, sortKey, direction) => {
        if (!sortKey) return data;
        
        return [...data].sort((a, b) => {
            let aValue = a[sortKey];
            let bValue = b[sortKey];
            
            // 권한(userRole) 정렬 - 우선순위 부여
            if (sortKey === 'userRole') {
                const roleOrder = { 'MASTER': 1, 'MANAGER': 2, 'STAFF': 3 };
                aValue = roleOrder[aValue] || 999;
                bValue = roleOrder[bValue] || 999;
            }
            // 날짜 문자열 처리
            else if (sortKey.includes('Date') || sortKey.includes('date')) {
                aValue = new Date(aValue || 0);
                bValue = new Date(bValue || 0);
            }
            // 문자열 정렬 - 한글, 영문, 숫자 순
            else if (typeof aValue === 'string' && typeof bValue === 'string') {
                const getCharType = (char) => {
                    if (!char) return 3;
                    const code = char.charCodeAt(0);
                    if (code >= 0xAC00 && code <= 0xD7A3) return 0; // 한글
                    if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) return 1; // 영문
                    if (code >= 48 && code <= 57) return 2; // 숫자
                    return 3; // 기타
                };
                
                const aType = getCharType(aValue.charAt(0));
                const bType = getCharType(bValue.charAt(0));
                
                if (aType !== bType) {
                    return direction === 'asc' ? aType - bType : bType - aType;
                }
                
                // 같은 타입이면 일반 문자열 비교
                return direction === 'asc' 
                    ? aValue.localeCompare(bValue, 'ko') 
                    : bValue.localeCompare(aValue, 'ko');
            }
            
            if (aValue < bValue) {
                return direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };
    
    // 필터링과 정렬을 함께 적용
    const applyFilterAndSort = (data, filterValue, sortConf) => {
        let filtered;
        
        if (filterValue === 'all') {
            filtered = data;
        } else if (filterFunction) {
            filtered = data.filter(item => filterFunction(item, filterValue));
        } else {
            filtered = data;
        }
        
        // 정렬 적용
        if (sortConf.key) {
            filtered = sortData(filtered, sortConf.key, sortConf.direction);
        }
        
        setFilteredData(filtered);
    };
    
    // 필터 적용
    const applyFilter = (filterValue, customFilterFunction = null) => {
        setCurrentFilter(filterValue);
        
        let filtered;
        if (filterValue === 'all') {
            filtered = originalData;
        } else if (customFilterFunction) {
            filtered = originalData.filter(item => customFilterFunction(item, filterValue));
        } else if (filterFunction) {
            filtered = originalData.filter(item => filterFunction(item, filterValue));
        } else {
            filtered = originalData;
        }
        
        // 현재 정렬 설정 유지하면서 필터 적용
        if (sortConfig.key) {
            filtered = sortData(filtered, sortConfig.key, sortConfig.direction);
        }
        
        setFilteredData(filtered);
        pagination.resetPage();
    };
    
    // 정렬 적용
    const applySort = (sortKey) => {
        let direction = 'asc';
        if (sortConfig.key === sortKey && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        const newSortConfig = { key: sortKey, direction };
        setSortConfig(newSortConfig);
        
        // 현재 필터링된 데이터에 정렬 적용
        applyFilterAndSort(originalData, currentFilter, newSortConfig);
        pagination.resetPage();
    };
    
    // 원본 데이터 업데이트
    const updateOriginalData = (newData) => {
        // 현재 필터와 정렬 설정을 유지하면서 데이터 업데이트
        applyFilterAndSort(newData, currentFilter, sortConfig);
    };
    
    return {
        ...pagination,
        filteredData,
        currentFilter,
        sortConfig,
        applyFilter,
        applySort,
        updateOriginalData
    };
};
